import { useState, type ReactNode } from 'react';
import { ChevronDownIcon, UpIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface TableColumn<T> {
  readonly key: string;
  readonly header: ReactNode;
  readonly cell: (row: T, rowIndex: number) => ReactNode;
  readonly sortable?: boolean;
  /** Comparator for sorting; receives the row, must return a primitive. Defaults to `cell` result. */
  readonly sortAccessor?: (row: T) => string | number;
  readonly align?: 'start' | 'center' | 'end';
  readonly width?: string;
  /** Sticky column on horizontal scroll. */
  readonly sticky?: boolean;
}

export interface TableProps<T> {
  readonly data: readonly T[];
  readonly columns: readonly TableColumn<T>[];
  readonly rowKey: (row: T) => string;
  /** Selectable rows — controlled selection. */
  readonly selectable?: boolean;
  readonly selectedIds?: ReadonlySet<string>;
  readonly onSelectionChange?: (ids: ReadonlySet<string>) => void;
  readonly onRowPress?: (row: T) => void;
  readonly empty?: ReactNode;
  /** Sticky header on vertical scroll. */
  readonly stickyHeader?: boolean;
  readonly className?: string;
  readonly density?: 'cosy' | 'compact';
  readonly ariaLabel?: string;
}

type SortDir = 'asc' | 'desc' | null;

const ALIGN: Record<NonNullable<TableColumn<unknown>['align']>, string> = {
  start: 'text-start',
  center: 'text-center',
  end: 'text-end',
};

const ROW_HEIGHT = { cosy: 'h-12', compact: 'h-9' } as const;
const CELL_PAD = { cosy: 'px-3 py-3', compact: 'px-3 py-1.5' } as const;

export function Table<T>({
  data,
  columns,
  rowKey,
  selectable,
  selectedIds,
  onSelectionChange,
  onRowPress,
  empty,
  stickyHeader,
  className,
  density = 'cosy',
  ariaLabel,
}: TableProps<T>) {
  const { t } = useI18n();
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const sorted = (() => {
    if (!sortKey || !sortDir) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortable) return data;
    const get = col.sortAccessor ?? ((row: T) => String(col.cell(row, 0)));
    const arr = data.slice();
    arr.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  const requestSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
  };

  const ids = selectedIds ?? new Set<string>();
  const allSelected = selectable && sorted.length > 0 && sorted.every((r) => ids.has(rowKey(r)));
  const someSelected = selectable && sorted.some((r) => ids.has(rowKey(r))) && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      const next = new Set(ids);
      sorted.forEach((r) => next.delete(rowKey(r)));
      onSelectionChange(next);
    } else {
      const next = new Set(ids);
      sorted.forEach((r) => next.add(rowKey(r)));
      onSelectionChange(next);
    }
  };

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    const next = new Set(ids);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  return (
    <div
      className={cn(
        'w-full min-w-0 max-w-full overflow-auto rounded-xl border border-hairline bg-surface',
        className,
      )}
    >
      <table className="w-full border-collapse text-[12.5px]" aria-label={ariaLabel}>
        <thead
          className={cn(
            'bg-surface-2 text-ink-2 font-mono uppercase tracking-wider text-caption',
            stickyHeader && 'sticky top-0 z-10',
          )}
        >
          <tr>
            {selectable ? (
              <th className={cn('w-10', CELL_PAD[density])}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected || false;
                  }}
                  onChange={toggleAll}
                  aria-label={
                    allSelected
                      ? t('common:table.deselectAll', { defaultValue: 'Deselect all' })
                      : t('common:table.selectAll', { defaultValue: 'Select all' })
                  }
                  className="size-4 accent-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                />
              </th>
            ) : null}
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                aria-sort={
                  col.sortable
                    ? sortKey === col.key && sortDir
                      ? sortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                    : undefined
                }
                style={col.width ? { width: col.width } : undefined}
                className={cn(
                  CELL_PAD[density],
                  ALIGN[col.align ?? 'start'],
                  'border-b border-hairline whitespace-nowrap',
                  col.sticky && 'sticky start-0 bg-surface-2 z-10',
                )}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => requestSort(col.key)}
                    className="inline-flex items-center gap-1 rounded-sm hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                  >
                    {col.header}
                    <span aria-hidden="true" className="opacity-60">
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? (
                          <UpIcon size={11} color="currentColor" />
                        ) : sortDir === 'desc' ? (
                          <ChevronDownIcon size={11} color="currentColor" />
                        ) : null
                      ) : null}
                    </span>
                  </button>
                ) : (
                  col.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-ink">
          {sorted.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="text-center py-8 text-ink-2"
              >
                {empty ?? t('common:table.empty', { defaultValue: 'No results' })}
              </td>
            </tr>
          ) : (
            sorted.map((row, rowIndex) => {
              const id = rowKey(row);
              const selected = ids.has(id);
              return (
                <tr
                  key={id}
                  tabIndex={onRowPress ? 0 : undefined}
                  aria-selected={selectable ? selected : undefined}
                  onClick={(event) => {
                    if (
                      event.target instanceof Element &&
                      event.target.closest(
                        'a, button, input, select, textarea, [role="button"], [role="link"]',
                      )
                    )
                      return;
                    onRowPress?.(row);
                  }}
                  onKeyDown={(event) => {
                    if (!onRowPress || event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowPress(row);
                    }
                  }}
                  className={cn(
                    ROW_HEIGHT[density],
                    'border-b border-hairline last:border-b-0',
                    selected && 'bg-accent-soft/40',
                    onRowPress &&
                      'cursor-pointer hover:bg-surface-2 transition-colors duration-(--duration-tap) motion-reduce:transition-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent-text',
                  )}
                >
                  {selectable ? (
                    <td
                      className={cn(CELL_PAD[density], 'w-10')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleRow(id)}
                        aria-label={
                          selected
                            ? t('common:table.deselectRow', { defaultValue: 'Deselect row' })
                            : t('common:table.selectRow', { defaultValue: 'Select row' })
                        }
                        className="size-4 accent-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                      />
                    </td>
                  ) : null}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        CELL_PAD[density],
                        ALIGN[col.align ?? 'start'],
                        col.sticky &&
                          (selected ? 'sticky start-0 bg-surface-2' : 'sticky start-0 bg-surface'),
                      )}
                    >
                      {col.cell(row, rowIndex)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
